import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@nexus/ui";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getDefaultRoute } from "@/features/auth/lib/page-routes";
import {
  DEFAULT_EXPLORE_SEARCH,
  EXPLORE_ROUTES,
} from "@/features/dashboard/lib/explore-search";
import type { TenantPermissions } from "@/features/auth/lib/api";

export function resolveNotFoundHome(options: {
  tenants: TenantPermissions[];
  activeTenantId: string | null;
}): { to: string; search?: typeof DEFAULT_EXPLORE_SEARCH } {
  const activeTenants = options.tenants.filter((tenant) => tenant.id === options.activeTenantId);
  const to = getDefaultRoute(activeTenants) ?? "/login";
  if (EXPLORE_ROUTES.has(to)) {
    return { to, search: DEFAULT_EXPLORE_SEARCH };
  }
  return { to };
}

function homeCtaKey(destination: string): string {
  if (destination === "/login") return "errors.backToLogin";
  if (destination === "/dashboard") return "errors.goHome";
  return "errors.goToAvailablePage";
}

export function NotFoundPage() {
  const { t } = useTranslation();
  const tenants = useAuth((s) => s.tenants);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const home = resolveNotFoundHome({ tenants, activeTenantId });

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
      <p className="text-7xl font-normal tracking-tight text-[var(--muted-foreground)]/30">404</p>
      <h1 className="text-xl font-normal">{t("errors.pageNotFound")}</h1>
      <p className="max-w-sm text-sm text-[var(--muted-foreground)]">
        {t("errors.pageNotFoundDescription")}
      </p>
      <Button asChild>
        <Link to={home.to as never} search={home.search as never}>
          {t(homeCtaKey(home.to))}
        </Link>
      </Button>
    </div>
  );
}
